import React, { useEffect, useRef, useState } from 'react';
import DatePicker from '../../../../component/library/datePicker';
import moment from 'moment';
import { orderApiService } from '../../../../service/seller/orderApiService';
import { WalletApiService } from '../../../../service/seller/walletApiService';
import { useLoader } from '../../../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import Pagination from '../../../../component/library/pagination';
import { MdModeEdit, MdPayment, MdCancel, MdDownload, MdSupport } from 'react-icons/md';
import { FaCreditCard } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import EditOrderPopup from '../../../../component/views/seller/orders/list/editOrderPopup';
import RaiseTicket from '../../../../component/views/seller/orderDetails/raiseTicketPopup';
import CancelOrderModal from '../../../../component/views/seller/orders/cancelOrderModal';
import PaymentModal from '../../../../component/views/seller/orders/paymentModal';
import ExcelTable from '../../../../component/library/excelTable';
import { exportToExcel } from '../../../../utils/excelExport';
import OrderFilter from '../../../../component/views/seller/orders/orderFilter';
import TicketChatPanel from '../../../../component/library/ticketChatPanel';
import FailedOrdersTable from '../../../../component/views/seller/orders/failedOrdersTable';
import './style.css';

const calculateTotalQuantity = order => {
  if (Number.isFinite(Number(order?.totalQuantity))) {
    return Number(order.totalQuantity);
  }

  if (Array.isArray(order?.orderDetails)) {
    return order.orderDetails.reduce((sum, detail) => {
      const quantity = Number(detail?.quantity);
      return sum + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
    }, 0);
  }

  return 0;
};

const calculateImporterrPrice = order => {
  // Check if order has wholesale breakdown at top level
  const hasWholesaleBreakdown = Array.isArray(order?.breakdown) && 
    order.breakdown.length > 0 && 
    order.breakdown[0]?.breakdown?.[0]?.FBP;

  if (hasWholesaleBreakdown) {
    // Use mkp_current_total_price for wholesale orders
    const mkpPrice = Number(order?.mkp_current_total_price);
    return Number.isFinite(mkpPrice) && mkpPrice > 0 ? mkpPrice : 0;
  }

  // Fallback to existing one-piece logic
  if (!Array.isArray(order?.orderDetails)) {
    const fallback = Number(order?.totalPrice) ?? Number(order?.totalConsumerPrice) ?? 0;
    return Number.isFinite(fallback) ? fallback : 0;
  }

  // Helper function to get price - prioritize finalPrice over breakdown.FBP
  const getItemPrice = (detail) => {
    const variant = detail?.variant;
    // Priority: finalPrice > breakdown.FBP
    if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
      return Number(variant.finalPrice);
    }
    const breakdown = variant?.breakdown;
    return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
  };

  let hasPrice = false;
  const total = order.orderDetails.reduce((sum, detail) => {
    const price = getItemPrice(detail);

    if (!Number.isFinite(price) || price === 0) {
      return sum;
    }

    hasPrice = true;
    const quantity = Number(detail?.quantity);
    const validQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    return sum + price * validQuantity;
  }, 0);

  if (hasPrice) {
    return total;
  }

  const fallback = Number(order?.totalPrice) ?? Number(order?.totalConsumerPrice);
  if (Number.isFinite(fallback)) {
    return fallback;
  }

  return order.orderDetails.reduce((acc, detail) => {
    const consumerPrice = Number(detail?.consumerPrice);
    return acc + (Number.isFinite(consumerPrice) ? consumerPrice : 0);
  }, 0);
};

const formatOrderForExport = order => {
  const totalQuantity = calculateTotalQuantity(order);
  const importerrPrice = calculateImporterrPrice(order);
  const shippingCharges = Number(order?.totalShippingCharges) || 0;
  const imPointsDiscount = Number(order?.imPointsDiscount) || 0;
  const importerrPriceWithShipping = importerrPrice + shippingCharges - imPointsDiscount;
  const mkpTotalWithShipping = (Number(order?.mkp_current_total_price) || 0) + shippingCharges - imPointsDiscount;
  const mkpPaidWithShipping = (Number(order?.mkp_current_received_price) || 0) + shippingCharges - imPointsDiscount;

  // Get mkp_financial_status from last item in mkpOrderStatusHistory array
  const getMkpFinancialStatus = (order) => {
    if (Array.isArray(order?.mkpOrderStatusHistory) && order.mkpOrderStatusHistory.length > 0) {
      const lastItem = order.mkpOrderStatusHistory[order.mkpOrderStatusHistory.length - 1];
      return lastItem?.mkp_order_status || order?.mkp_financial_status || '';
    }
    return order?.mkp_financial_status || '';
  };

  return {
    upsaleoOrderId: order?.upsaleoOrderId || '',
    orderId: order?.orderId || '',
    totalQuantity,
    totalPrice: importerrPriceWithShipping,
    imPointsDiscount: imPointsDiscount > 0 ? `₹${imPointsDiscount.toFixed(2)}` : '₹0.00',
    marketplace: order?.marketplace || '',
    mkp_current_total_price: mkpTotalWithShipping,
    mkp_financial_status: getMkpFinancialStatus(order),
    mkp_current_received_price: mkpPaidWithShipping,
    mkp_current_outstanding_price: Number(order?.mkp_current_outstanding_price) || 0,
    orderDate: order?.orderDate ? new Date(order.orderDate) : '',
    updatedAt: order?.updatedAt ? new Date(order.updatedAt) : '',
    status: order?.status || '',
  };
};

const ORDER_EXPORT_COLUMNS = [
  { key: 'upsaleoOrderId', label: 'Importerr Order ID', width: 160 },
  { key: 'orderId', label: 'MKP Order ID', width: 140 },
  { key: 'totalQuantity', label: 'Quantity', width: 100 },
  { key: 'totalPrice', label: 'Importerr Price', width: 130 },
  { key: 'imPointsDiscount', label: 'IM Points Discount', width: 150 },
  { key: 'marketplace', label: 'MKP Name', width: 120 },
  { key: 'mkp_current_total_price', label: 'MKP Total Price', width: 140 },
  { key: 'mkp_financial_status', label: 'MKP Payment Status', width: 150 },
  { key: 'mkp_current_received_price', label: 'MKP Paid', width: 120 },
  { key: 'mkp_current_outstanding_price', label: 'MKP Balance', width: 120 },
  { key: 'orderDate', label: 'Order Date', width: 180 },
  { key: 'updatedAt', label: 'Updated at', width: 180 },
  { key: 'status', label: 'Status', width: 120 },
];

/**
 * NEW: Build orderType counts from the current orders list.
 * Used only when a date range filter is applied, so other flows remain unchanged.
 */
const buildOrderTypeFromOrders = (orders = []) => {
  const initial = {
    totalOrders: 0,
    abnormalOrder: 0,
    paymentPending: 0,
    processingOrder: 0,
    shippedOrder: 0,
    deliveredOrder: 0,
    cancelOrder: 0,
    ticketOrder: 0,
  };

  return orders.reduce((acc, order) => {
    const status = (order.status || '').toLowerCase();

    acc.totalOrders += 1;

    switch (status) {
      case 'abnormal':
        acc.abnormalOrder += 1;
        break;
      case 'payment pending':
        acc.paymentPending += 1;
        break;
      case 'processing':
        acc.processingOrder += 1;
        break;
      case 'shipped':
        acc.shippedOrder += 1;
        break;
      case 'delivered':
        acc.deliveredOrder += 1;
        break;
      case 'cancelled':
        acc.cancelOrder += 1;
        break;
      default:
        break;
    }

    // ticket count based on flag / ticketId
    if (order.ticket || order.ticketId) {
      acc.ticketOrder += 1;
    }

    return acc;
  }, initial);
};

const Orders = () => {
  const [data, setData] = useState([]);
  const [orderType, setOrderType] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState({
    searchFields: "",
    startDate: "",
    endDate: "",
  });
  const [uploadFile, setUploadFile] = useState('');
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState('');
  const [uploadFileErr, setUploadFileErr] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [activeTicketOrderId, setActiveTicketOrderId] = useState(null);
  const [ticketOrderContext, setTicketOrderContext] = useState({
    bulkIds: [],
    processingIds: [],
  });
  
  // Failed orders state
  const [showFailedOrders, setShowFailedOrders] = useState(false);
  const [failedOrders, setFailedOrders] = useState([]);
  const [failedOrdersCount, setFailedOrdersCount] = useState(0);
  const [isOwnAccount, setIsOwnAccount] = useState(null);
  const [failedPage, setFailedPage] = useState(1);
  const [failedPages, setFailedPages] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
  });
  
  // Cancel order modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelOrderData, setCancelOrderData] = useState(null); // Single order
  const [cancelOrdersData, setCancelOrdersData] = useState([]); // Multiple orders
  const [isCancelling, setIsCancelling] = useState(false);
  const [ticketPanelTicketId, setTicketPanelTicketId] = useState(null);
  const [ticketPanelOrderLabel, setTicketPanelOrderLabel] = useState(null);

  // Payment modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentOrderData, setPaymentOrderData] = useState(null); // Single order
  const [paymentOrdersData, setPaymentOrdersData] = useState([]); // Multiple orders
  const [isPayingOrder, setIsPayingOrder] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);

  const loader = useLoader();
  const message = useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const orderFileRef = useRef(null);

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);

  const [pages, setPages] = useState({
    currentPage: '',
    totalPages: '',
    totalItems: '',
  });

  const options = [
    {
      name: 'All Types',
      value: '',
      count: orderType.totalOrders,
    },
    {
      name: 'Abnormal',
      value: 'abnormal',
      count: orderType.abnormalOrder,
    },
    {
      name: 'Payment Pending',
      value: 'payment pending',
      count: orderType.paymentPending,
    },
    {
      name: 'Processing',
      value: 'processing',
      count: orderType.processingOrder,
    },
    {
      name: 'Shipped',
      value: 'shipped',
      count: orderType.shippedOrder,
    },
    {
      name: 'Delivered',
      value: 'delivered',
      count: orderType.deliveredOrder,
    },
    {
      name: 'Cancelled',
      value: 'cancelled',
      count: orderType.cancelOrder,
    },
    {
      name: 'Ticket',
      value: 'ticket',
      count: orderType.ticketOrder,
    },
  ];

  // Build query params from query state
  const queryParams = `?status=${status}${query.startDate ? '&startDate=' + query.startDate : ''}${query.endDate ? '&endDate=' + query.endDate : ''}${query.searchFields ? '&searchFields=' + encodeURIComponent(query.searchFields) : ''}&limit=${limit}&page=${page}`;
  
  useEffect(() => {
    const search = new URLSearchParams(location.search);

    if (search.get('status')) {
      const urlStatus = search.get('status');
      setStatus(urlStatus);
      setQuery(prev => ({ ...prev, status: urlStatus }));
    }
  }, []);

  // Track previous query to detect filter changes
  const prevQueryRef = useRef(query);
  
  useEffect(() => {
    // Check if filters changed (excluding page)
    const filtersChanged = 
      prevQueryRef.current.searchFields !== query.searchFields ||
      prevQueryRef.current.startDate !== query.startDate ||
      prevQueryRef.current.endDate !== query.endDate;
    
    // Reset page to 1 if filters changed
    if (filtersChanged && page !== 1) {
      setPage(1);
      prevQueryRef.current = query;
      return; // Don't fetch yet, let the page change trigger fetch
    }
    
    prevQueryRef.current = query;
    fetchOrders();
    const search = new URLSearchParams(location.search);
    if (search.size) {
      navigate('/seller/orders');
    }
  }, [query, page, status]);
  
  // Sync query state with startDate, endDate for backward compatibility
  useEffect(() => {
    if (startDate && !query.startDate) {
      setQuery(prev => ({ ...prev, startDate }));
    }
    if (endDate && !query.endDate) {
      setQuery(prev => ({ ...prev, endDate }));
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!ticketModalOpen) {
      setTicketOrderContext({
        bulkIds: [],
        processingIds: [],
      });
      setActiveTicketOrderId(null);
    }
  }, [ticketModalOpen]);

  const fetchOrders = async () => {
    await loader.showLoading(true);
    orderApiService
      .getAllOrderList(queryParams)
      .then(res => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          const orders = res?.data?.orders || [];

          setData(orders);

          // NEW: when date range is applied, compute counts from filtered orders.
          // Otherwise, keep using API-provided counts (existing behavior).
          const isDateFiltered = !!(startDate && endDate);
          const typeData = isDateFiltered
            ? buildOrderTypeFromOrders(orders)
            : res?.data || {};

          setOrderType(typeData);

          setPages({
            ...pages,
            currentPage: res.data.currentPage,
            totalPages: res.data.totalPages,
            totalItems: res.data.totalOrders,
          });
          setPage(res.data.currentPage);
        } else {
          setData([]);
          setOrderType({});
          setPages({
            currentPage: '',
            totalPages: '',
            totalItems: '',
          });
          setPage(1);
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch(err => {
        loader.showLoading(false);
        message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
        setData([]);
        setOrderType({});
        setPages({
          currentPage: '',
          totalPages: '',
          totalItems: '',
        });
        setPage(1);
      });
  };

  // Fetch failed orders for own account sellers
  const fetchFailedOrders = async () => {
    const failedQueryParams = `?limit=${limit}&page=${failedPage}${startDate ? '&startDate=' + startDate : ''}${endDate ? '&endDate=' + endDate : ''}`;
    
    await loader.showLoading(true);
    orderApiService
      .getFailedOrders(failedQueryParams)
      .then(res => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          setIsOwnAccount(true);
          setFailedOrders(res.data.orders || []);
          setFailedOrdersCount(res.data.totalOrders || 0);
          setFailedPages({
            currentPage: res.data.currentPage || 1,
            totalPages: res.data.totalPages || 0,
            totalItems: res.data.totalOrders || 0,
          });
        } else if (res.status.code === 403) {
          setIsOwnAccount(false);
          setFailedOrders([]);
          setFailedOrdersCount(0);
        }
      })
      .catch(err => {
        loader.showLoading(false);
        if (err?.status === 403) {
          setIsOwnAccount(false);
        }
        setFailedOrders([]);
        setFailedOrdersCount(0);
      });
  };

  // Fetch failed orders count on mount (for own account sellers)
  useEffect(() => {
    fetchFailedOrders();
  }, []);

  // Refetch failed orders when page changes
  useEffect(() => {
    if (showFailedOrders) {
      fetchFailedOrders();
    }
  }, [failedPage, showFailedOrders]);

  const handleOrderType = value => {
    setStatus(value);
    setPage(1);
    
    // Auto-select all orders when payment pending tab is selected
    if (value === 'payment pending') {
      setTimeout(() => {
        const paymentPendingOrders = data.filter(order => order.status === 'payment pending');
        setSelectedOrders(paymentPendingOrders);
      }, 100);
    } else {
      setSelectedOrders([]);
    }
  };

  const handleOrdersFile = e => {
    setUploadFile(e.target.files[0]);
    setUploadFileErr('');
  };

  const validateUploadFile = () => {
    let result;

    if (!uploadFile) {
      setUploadFileErr('File is required');
      result = false;
    } else {
      result = true;
    }
    return result;
  };

  const handleUploadOrders = () => {
    if (validateUploadFile()) {
      loader.showLoading(true);

      let filePayload = new FormData();
      filePayload.append('file', uploadFile);

      orderApiService
        .addBulkOrders(filePayload)
        .then(res => {
          loader.showLoading(false);
          if (res.status.code === 200) {
            message.showToast(res.status.message, ToastTypes.SUCCESS);
            setUploadFile();
            fetchOrders();
            orderFileRef.current.value = '';
          } else if (res.status.code === 400) {
            res.data.map(item => {
              message.showToast(item, ToastTypes.ERROR);
            });
          } else {
            message.showToast(res.error.message, ToastTypes.ERROR);
          }
        })
        .catch(err => {
          message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
          loader.showLoading(false);
        });
    }
  };

  const handleOrderById = id => {
    navigate(`/seller/orders/orderDetails/${id}`);
  };

  const handleEdit = id => {
    setOpen(true);
    setActiveId(id);
  };

  const jsonToCsv = () => {
    // Writing CSV headers
    let csvContent =
      'order-id,order-item-id,sku,quantity-purchased,consumer-price,recipient-name,buyer-phone-number,ship-address-1,ship-address-2,ship-address-3,ship-city,ship-state,ship-postal-code' +
      '\n';

    // Writing CSV data
    return csvContent;
  };

  function downloadCsv(csvContent, fileName) {
    // Create a Blob object
    const blob = new Blob([csvContent], { type: 'text/csv' });

    // Create a download URL
    const url = window.URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);

    // Append the link to the body
    document.body.appendChild(link);

    // Click the link to trigger download
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  const handleDownloadCsv = () => {
    downloadCsv(jsonToCsv(), 'data.csv');
  };

  const handleUploadOneOrder = () => {
    navigate('/seller/orders/addOrder');
  };

  const downloadAbnormalOrders = () => {
    orderApiService
      .getAbnormalOrders()
      .then(res => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          const url = res.data.url;
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'products.csv');
          link.click();
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch(err => {
        loader.showLoading(false);
        message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
      });
  };

  const handleExportAllOrders = () => {
    loader.showLoading(true);
    const queryParams = `?status=${status}&startDate=${startDate}&endDate=${endDate}`;
    
    orderApiService
      .exportAllOrders(queryParams)
      .then((res) => {
        loader.showLoading(false);
        if (res.status === 200) {
          // res.data is already a blob
          const blob = res.data;
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          
          // Generate filename with current date
          const now = new Date();
          const day = String(now.getDate()).padStart(2, "0");
          const month = String(now.getMonth() + 1).padStart(2, "0");
          const year = now.getFullYear();
          const formattedDate = `${day}${month}${year}`;
          const fileName = `all-orders-export-${formattedDate}.xlsx`;
          
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          message.showToast("Orders exported successfully!", ToastTypes.SUCCESS);
        } else {
          message.showToast("Failed to export orders", ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        
        if (err.status === 402) {
          message.showToast("Authentication failed. Please login again.", ToastTypes.ERROR);
        } else if (err.status === 403) {
          // Check the specific error message to differentiate between inactive and expired membership
          if (err.error && err.error.message) {
            message.showToast(err.error.message, ToastTypes.ERROR);
          } else {
            message.showToast("Membership is not active. Please activate your membership to export orders.", ToastTypes.ERROR);
          }
        } else if (err.status === 404) {
          message.showToast("No orders found to export", ToastTypes.WARNING);
        } else if (err.error && err.error.message) {
          message.showToast(err.error.message, ToastTypes.ERROR);
        } else {
          message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
        }
      });
  };

  const handleBulkAction = (actionKey, selectedCount) => {
    if (actionKey === 'makePayment') {
      handleBulkPayment(selectedOrders);
    } else if (actionKey === 'cancelOrders') {
      handleBulkCancel(selectedOrders);
    } else if (actionKey === 'downloadOrders') {
      handleBulkDownload(selectedOrders);
    } else if (actionKey === 'raiseTicket') {
      handleBulkTicket(selectedOrders);
    }
  };

  const handleBulkPayment = async (selectedOrdersList) => {
    if (selectedOrdersList.length === 0) {
      message.showToast('Please select orders to make payment', ToastTypes.WARNING);
      return;
    }

    const paymentPendingOrders = selectedOrdersList.filter(order => 
      order.status === 'payment pending'
    );

    if (paymentPendingOrders.length === 0) {
      message.showToast('No payment pending orders selected', ToastTypes.WARNING);
      return;
    }

    // Open payment modal for bulk payment
    await fetchWalletBalance();
    setPaymentOrderData(null);
    setPaymentOrdersData(paymentPendingOrders);
    setPaymentModalOpen(true);
  };

  const handleBulkCancel = async (selectedOrders) => {
    if (selectedOrders.length === 0) {
      message.showToast('Please select orders to cancel', ToastTypes.WARNING);
      return;
    }

    // Filter cancellable orders:
    // - payment pending orders can always be cancelled
    // - processing orders can only be cancelled within 30 minutes of payment
    const cancellableOrders = selectedOrders.filter(order => {
      if (order.status === 'payment pending') return true;
      if (order.status === 'processing' && isWithin30Minutes(order)) return true;
      return false;
    });

    if (cancellableOrders.length === 0) {
      message.showToast('No cancellable orders selected. Processing orders can only be cancelled within 30 minutes of payment.', ToastTypes.WARNING);
      return;
    }

    // Fetch full order details for each cancellable order to get product info
    try {
      const fullOrdersPromises = cancellableOrders.map(async (order) => {
        try {
          const res = await orderApiService.getOrderById(order._id);
          if (res.status.code === 200 && res.data) {
            return res.data;
          }
          return order; // Fallback to original order data
        } catch (err) {
          return order; // Fallback to original order data
        }
      });
      
      const fullOrders = await Promise.all(fullOrdersPromises);
      
      // Open modal with multiple orders (with full product info)
      setCancelOrderData(null);
      setCancelOrdersData(fullOrders);
      setCancelModalOpen(true);
    } catch (err) {
      // Fallback to original order data if fetch fails
      setCancelOrderData(null);
      setCancelOrdersData(cancellableOrders);
      setCancelModalOpen(true);
    }
  };

  const handleBulkDownload = async (selectedOrders) => {
    if (selectedOrders.length === 0) {
      message.showToast('Please select orders to download', ToastTypes.WARNING);
      return;
    }

    loader.showLoading(true);

    try {
      const exportRows = selectedOrders.map(formatOrderForExport);
      const result = await exportToExcel(exportRows, ORDER_EXPORT_COLUMNS, 'selected-orders');

      if (result?.success) {
        message.showToast(`${selectedOrders.length} orders downloaded successfully`, ToastTypes.SUCCESS);
      } else {
        throw new Error(result?.error || 'Failed to export selected orders');
      }
    } catch (err) {
      const errorMessage =
        err?.message === 'Failed to export selected orders'
          ? err.message
          : err?.message || 'Failed to download orders';
      message.showToast(errorMessage, ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const handleBulkTicket = (selectedOrders) => {
    if (selectedOrders.length === 0) {
      message.showToast('Please select orders to raise tickets', ToastTypes.WARNING);
      return;
    }

    const ticketableOrders = selectedOrders.filter(order => 
      ['processing', 'shipped', 'partially-shipped', 'delivered'].includes(order.status)
    );

    if (ticketableOrders.length === 0) {
      message.showToast('Tickets can only be raised for processing, shipped, partially-shipped, or delivered orders', ToastTypes.WARNING);
      return;
    }

    const bulkEligibleOrders = ticketableOrders.filter(order =>
      ['shipped', 'partially-shipped', 'delivered'].includes(order.status)
    );
    const processingEligibleOrders = ticketableOrders.filter(order => order.status === 'processing');

    const ineligibleCount = selectedOrders.length - (bulkEligibleOrders.length + processingEligibleOrders.length);
    if (ineligibleCount > 0) {
      message.showToast(`${ineligibleCount} selected order(s) are not eligible for ticket raising and will be skipped`, ToastTypes.WARNING);
    }

    setTicketOrderContext({
      bulkIds: bulkEligibleOrders.map(order => order._id),
      processingIds: processingEligibleOrders.map(order => order._id),
    });
    setActiveTicketOrderId(null);
    setTicketModalOpen(true);
  };

  // Fetch IM cash balance
  const fetchWalletBalance = async () => {
    try {
      const res = await WalletApiService.getWalet();
      if (res.status.code === 200) {
        // Handle different response structures (same as tabs component)
        let balance = 0;
        if (res.data?.balance !== undefined) {
          balance = res.data.balance;
        } else if (res.data?.wallet?.balance !== undefined) {
          balance = res.data.wallet.balance;
        } else if (typeof res.data === 'number') {
          balance = res.data;
        }
        setWalletBalance(Number(balance) || 0);
      }
    } catch (err) {
      console.error('Failed to fetch IM cash:', err);
    }
  };

  // Individual order actions - Open payment modal
  const handlePayOrder = async (order) => {
    await fetchWalletBalance();
    setPaymentOrderData(order);
    setPaymentOrdersData([]);
    setPaymentModalOpen(true);
  };

  // Handle payment confirmation from modal
  const handlePaymentConfirm = async (orderIds, paymentMethod) => {
    setIsPayingOrder(true);
    
    try {
      if (paymentMethod === 'razorpay') {
        // Handle Razorpay payment
        if (orderIds.length > 1) {
          message.showToast('Razorpay payment is only available for single orders. Please select one order at a time.', ToastTypes.WARNING);
          setIsPayingOrder(false);
          return;
        }
        
        const orderId = orderIds[0];
        const res = await orderApiService.PayOrder(orderId, paymentMethod);
        
        if (res.status.code === 200 && res.data?.razorpayOrderId) {
          // Open Razorpay checkout
          await openRazorpayCheckout(res.data, orderId);
        } else {
          setIsPayingOrder(false);
          message.showToast(res.error?.message || res.status?.message || 'Failed to create Razorpay order', ToastTypes.ERROR);
        }
        return;
      }
      
      // Wallet payment flow
      if (orderIds.length === 1) {
        // Single order payment
        const res = await orderApiService.PayOrder(orderIds[0], paymentMethod);
        setIsPayingOrder(false);
        if (res.status.code === 200) {
          message.showToast('Payment processed successfully', ToastTypes.SUCCESS);
          setPaymentModalOpen(false);
          setPaymentOrderData(null);
          setPaymentOrdersData([]);
          setSelectedOrders([]);
          fetchOrders();
        } else if (res.status.code === 402) {
          message.showToast('Insufficient IM Cash balance. Please recharge your IM Cash.', ToastTypes.ERROR);
        } else {
          message.showToast(res.error?.message || res.status?.message || 'Payment failed', ToastTypes.ERROR);
        }
      } else {
        // Multiple orders payment
        const paymentData = { orderIds, paymentMethod };
        const res = await orderApiService.makeBulkPayment(paymentData);
        setIsPayingOrder(false);
        if (res.status.code === 200) {
          message.showToast(`${orderIds.length} orders paid successfully`, ToastTypes.SUCCESS);
          setPaymentModalOpen(false);
          setPaymentOrderData(null);
          setPaymentOrdersData([]);
          setSelectedOrders([]);
          fetchOrders();
        } else if (res.status.code === 402) {
          message.showToast('Insufficient IM Cash balance. Please recharge your IM Cash.', ToastTypes.ERROR);
        } else {
          message.showToast(res.error?.message || res.status?.message || 'Payment failed', ToastTypes.ERROR);
        }
      }
    } catch (err) {
      setIsPayingOrder(false);
      // Handle error from rejected promise (non-2xx status codes)
      const errorStatus = err?.status || err?.data?.status?.code;
      const errorMessage = err?.data?.error?.message || err?.data?.status?.message || err?.message || 'Failed to process payment';
      
      if (errorStatus === 402) {
        message.showToast('Insufficient IM Cash balance. Please recharge your IM Cash.', ToastTypes.ERROR);
      } else {
        message.showToast(errorMessage, ToastTypes.ERROR);
      }
    }
  };

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Open Razorpay checkout
  const openRazorpayCheckout = async (razorpayData, orderId) => {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      setIsPayingOrder(false);
      message.showToast('Failed to load Razorpay. Please try again.', ToastTypes.ERROR);
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZOR_PAY_KEY,
      amount: razorpayData.amount,
      currency: razorpayData.currency || 'INR',
      order_id: razorpayData.razorpayOrderId,
      name: 'Importerr',
      description: `Payment for Order ${razorpayData.upsaleoOrderId}`,
      handler: async (response) => {
        // Verify payment on backend
        try {
          const verifyRes = await orderApiService.verifyRazorpayPayment({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            orderId: orderId
          });
          
          setIsPayingOrder(false);
          if (verifyRes.status.code === 200) {
            message.showToast('Payment successful!', ToastTypes.SUCCESS);
            setPaymentModalOpen(false);
            setPaymentOrderData(null);
            setPaymentOrdersData([]);
            setSelectedOrders([]);
            fetchOrders();
          } else {
            message.showToast(verifyRes.error?.message || 'Payment verification failed', ToastTypes.ERROR);
          }
        } catch (err) {
          setIsPayingOrder(false);
          message.showToast('Payment verification failed. Please contact support.', ToastTypes.ERROR);
        }
      },
      modal: {
        ondismiss: () => {
          setIsPayingOrder(false);
          message.showToast('Payment cancelled', ToastTypes.WARNING);
        }
      },
      prefill: {
        name: '',
        email: '',
        contact: ''
      },
      theme: {
        color: '#10b981'
      }
    };

    const paymentWindow = new window.Razorpay(options);
    paymentWindow.open();
  };

  // Open payment modal for multiple orders
  const openBulkPaymentModal = async () => {
    const payableOrders = selectedOrders
      .map(id => data.find(order => order._id === id))
      .filter(order => order && order.status === 'payment pending');
    
    if (payableOrders.length === 0) {
      message.showToast('No orders with payment pending status selected', ToastTypes.ERROR);
      return;
    }
    
    await fetchWalletBalance();
    setPaymentOrderData(null);
    setPaymentOrdersData(payableOrders);
    setPaymentModalOpen(true);
  };

  // Open cancel modal with single order details - fetch full order data with product info
  const openCancelModal = async (order) => {
    try {
      // Fetch full order details to get product info for accurate refund calculation
      const res = await orderApiService.getOrderById(order._id);
      if (res.status.code === 200 && res.data) {
        setCancelOrderData(res.data);
      } else {
        // Fallback to original order data if fetch fails
        setCancelOrderData(order);
      }
    } catch (err) {
      // Fallback to original order data if fetch fails
      setCancelOrderData(order);
    }
    setCancelOrdersData([]);
    setCancelModalOpen(true);
  };

  // Handle cancel order confirmation (supports single and multiple orders)
  const handleCancelOrder = async (orderIds) => {
    setIsCancelling(true);
    
    // Handle array of order IDs (extract just the IDs if objects are passed)
    const ids = Array.isArray(orderIds) 
      ? orderIds.map(item => typeof item === 'object' ? item.orderId : item)
      : [orderIds];
    
    try {
      if (ids.length === 1) {
        // Single order cancel
        const res = await orderApiService.CancelOrder(ids[0]);
        setIsCancelling(false);
        if (res.status.code === 200) {
          message.showToast('Order cancelled successfully. Refund credited to IM Cash.', ToastTypes.SUCCESS);
          setCancelModalOpen(false);
          setCancelOrderData(null);
          setCancelOrdersData([]);
          setSelectedOrders([]);
          fetchOrders();
        } else {
          // Extract error message from API response
          const errorMsg = res.status?.message || res.error?.message || 'Failed to cancel order';
          message.showToast(errorMsg, ToastTypes.ERROR);
        }
      } else {
        // Multiple orders cancel
        const cancelData = { orderIds: ids };
        const res = await orderApiService.cancelBulkOrders(cancelData);
        setIsCancelling(false);
        if (res.status.code === 200) {
          message.showToast(`${ids.length} orders cancelled successfully. Refunds credited to IM Cash.`, ToastTypes.SUCCESS);
          setCancelModalOpen(false);
          setCancelOrderData(null);
          setCancelOrdersData([]);
          setSelectedOrders([]);
          fetchOrders();
        } else {
          // Extract error message from API response
          const errorMsg = res.status?.message || res.error?.message || 'Failed to cancel orders';
          message.showToast(errorMsg, ToastTypes.ERROR);
        }
      }
    } catch (err) {
      setIsCancelling(false);
      message.showToast(err.error?.message || 'Failed to cancel order(s)', ToastTypes.ERROR);
    }
  };

  const handleRaiseTicketSingle = (order) => {
    if (!order) return;

    const { _id, status } = order;

    if (['shipped', 'partially-shipped', 'delivered'].includes(status)) {
      setTicketOrderContext({
        bulkIds: [_id],
        processingIds: [],
      });
      setActiveTicketOrderId(null);
    } else if (status === 'processing') {
      setTicketOrderContext({
        bulkIds: [],
        processingIds: [],
      });
      setActiveTicketOrderId(_id);
    } else {
      message.showToast('Tickets can only be raised for processing, shipped, partially-shipped, or delivered orders', ToastTypes.WARNING);
      return;
    }

    setTicketModalOpen(true);
  };

  const handleViewTicket = (ticketId, orderLabel) => {
    setTicketPanelTicketId(ticketId);
    setTicketPanelOrderLabel(orderLabel || null);
  };

  // Check if order is within 30 minutes of payment
  const isWithin30Minutes = (order) => {
    // Use paidDate for processing orders (when payment was made)
    const paidDate = order.paidDate;
    if (!paidDate) return false;
    
    const now = new Date();
    const paymentTime = new Date(paidDate);
    const diffInMinutes = (now - paymentTime) / (1000 * 60);
    return diffInMinutes <= 30; // 30 minutes
  };

  // Render action buttons based on order status and payment time
  const renderActionButtons = (order) => {
    const { status, _id, ticket, ticketId } = order;

    if (ticket && ticketId) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewTicket(ticketId, order.upsaleoOrderId);
          }}
          className="action-btn view-ticket-btn"
          title="View Ticket"
        >
          <MdSupport /> View Ticket
        </button>
      );
    }

    switch (status) {
      case 'payment pending':
        return (
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePayOrder(order);
              }}
              className="action-btn pay-btn"
              title="Pay Order"
            >
              <MdPayment /> Pay
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openCancelModal(order);
              }}
              className="action-btn cancel-btn"
              title="Cancel Order"
            >
              <MdCancel /> Cancel
            </button>
          </div>
        );
      
      case 'processing':
        // Show cancel button only within 30 minutes of payment
        if (isWithin30Minutes(order)) {
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openCancelModal(order);
              }}
              className="action-btn cancel-btn"
              title="Cancel Order (within 30 min)"
            >
              <MdCancel /> Cancel
            </button>
          );
        }
        // After 30 minutes, show raise ticket
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRaiseTicketSingle(order);
            }}
            className="action-btn ticket-btn"
            title="Raise Ticket"
          >
            <MdSupport /> Raise Ticket
          </button>
        );
      
      case 'shipped':
      case 'partially-shipped':
      case 'delivered':
        // Check if ticket already exists
        if (order.ticket && order.ticketId) {
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewTicket(order.ticketId, order.upsaleoOrderId);
              }}
              className="action-btn view-ticket-btn"
              title="View Ticket"
            >
              <MdSupport /> View Ticket
            </button>
          );
        }
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRaiseTicketSingle(order);
            }}
            className="action-btn ticket-btn"
            title="Raise Ticket"
          >
            <MdSupport /> Raise Ticket
          </button>
        );
      
      default:
        return <span className="text-gray-400">-</span>;
    }
  };

  // Dynamic bulk actions based on selected orders
  const getBulkActions = (selectedOrders) => {
    if (!selectedOrders || selectedOrders.length === 0) return [];
    
    const statuses = [...new Set(selectedOrders.map(order => order.status))];
    const actions = [];
    
    // If all selected orders are payment pending
    if (statuses.length === 1 && statuses[0] === 'payment pending') {
      actions.push(
        { key: 'makePayment', label: 'Make Payment', icon: <FaCreditCard /> },
        { key: 'cancelOrders', label: 'Cancel Orders', icon: <MdCancel /> }
      );
    }
    // If all selected orders are processing
    else if (statuses.length === 1 && statuses[0] === 'processing') {
      // Check if any orders are within 30 minutes (cancellable)
      const cancellableOrders = selectedOrders.filter(order => isWithin30Minutes(order));
      
      if (cancellableOrders.length > 0) {
        actions.push(
          { key: 'cancelOrders', label: `Cancel Orders (${cancellableOrders.length})`, icon: <MdCancel /> }
        );
      }
      actions.push(
        { key: 'downloadOrders', label: 'Download', icon: <MdDownload /> },
        { key: 'raiseTicket', label: 'Raise Ticket', icon: <MdSupport /> }
      );
    }
    // If all selected orders are shipped or partially-shipped
    else if (statuses.length === 1 && (statuses[0] === 'shipped' || statuses[0] === 'partially-shipped')) {
      actions.push(
        { key: 'downloadOrders', label: 'Download', icon: <MdDownload /> },
        { key: 'raiseTicket', label: 'Raise Ticket', icon: <MdSupport /> }
      );
    }
    // If mixed statuses (processing, pending, awaiting payment)
    else if (statuses.every(s => ['processing', 'payment pending', 'awaiting payment'].includes(s))) {
      // Check for cancellable orders in mixed selection
      const cancellableOrders = selectedOrders.filter(order => 
        order.status === 'payment pending' || 
        (order.status === 'processing' && isWithin30Minutes(order))
      );
      
      if (cancellableOrders.length > 0) {
        actions.push(
          { key: 'cancelOrders', label: `Cancel Orders (${cancellableOrders.length})`, icon: <MdCancel /> }
        );
      }
      actions.push(
        { key: 'downloadOrders', label: 'Download', icon: <MdDownload /> }
      );
    }
    // Default download for any selection
    else {
      actions.push(
        { key: 'downloadOrders', label: 'Download', icon: <MdDownload /> }
      );
    }
    
    return actions;
  };

  // Excel table column configuration
  const tableColumns = [
    {
      key: 'actions',
      label: 'Actions',
      width: 150,
      render: (value, row) => renderActionButtons(row)
    },
    {
      key: 'upsaleoOrderId',
      label: 'Importerr Order ID',
      width: 160,
      render: (value, row) => {
        // Check for customer note - check multiple possible field names
        const customerNote = row?.customerNote || row?.note || row?.customer_note;
        const hasNote = customerNote && String(customerNote).trim().length > 0;
        
        const handleMouseEnter = (e) => {
          if (!hasNote) return;
          const tooltip = e.currentTarget.querySelector('.customer-note-tooltip-card');
          if (tooltip) {
            const rect = e.currentTarget.getBoundingClientRect();
            tooltip.style.top = `${rect.bottom + 5}px`;
            tooltip.style.left = `${rect.left}px`;
          }
        };
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span className="cell-link" onClick={() => handleOrderById(row._id)}>
              {value}
            </span>
            {hasNote && (
              <div className="customer-note-tooltip-wrapper" onMouseEnter={handleMouseEnter}>
                <span 
                  className="customer-note-badge"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: '500',
                    backgroundColor: '#dbeafe',
                    color: '#1e40af',
                    borderRadius: '4px',
                    border: '1px solid #93c5fd',
                    cursor: 'pointer',
                  }}
                >
                  <i className="fas fa-sticky-note" style={{ fontSize: '8px' }}></i>
                  Note
                </span>
                <div className="customer-note-tooltip-card">
                  <div className="customer-note-tooltip-header">
                    <i className="fas fa-sticky-note" style={{ marginRight: '6px' }}></i>
                    <strong>Customer Note</strong>
                  </div>
                  <div className="customer-note-tooltip-content">
                    {String(customerNote)}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'orderId',
      label: 'MKP Order ID',
      width: 140,
      render: (value, row) => {
        // Shopify: swap UI only — MKP Order ID column shows Order Item ID value
        const isShopify = row?.marketplace?.toLowerCase() === 'shopify';
        const orderItemIds = row.orderDetails?.map(detail => detail?.orderItemId).filter(Boolean) || [];
        const uniqueOrderItemIds = [...new Set(orderItemIds)];
        const mkpDisplay = isShopify
          ? (uniqueOrderItemIds.length > 0 ? uniqueOrderItemIds.join(', ') : '-')
          : (value || '-');
        return (
          <span className="cell-link" onClick={() => handleOrderById(row._id)}>
            {mkpDisplay}
          </span>
        );
      }
    },
    {
      key: 'orderItemId',
      label: 'Order Item ID',
      width: 160,
      render: (value, row) => {
        // Get all orderItemIds from orderDetails
        const orderItemIds = row.orderDetails?.map(detail => detail?.orderItemId).filter(Boolean) || [];
        const uniqueIds = [...new Set(orderItemIds)];
        const orderItemDisplay = uniqueIds.length > 0 ? uniqueIds.join(', ') : '-';
        // Shopify: swap UI only — Order Item ID column shows MKP Order ID value
        const isShopify = row?.marketplace?.toLowerCase() === 'shopify';
        const mkpValue = row?.mkpOrderDisplayId || row?.orderId;
        const displayValue = isShopify ? (mkpValue || '-') : orderItemDisplay;
        return (
          <span className="cell-link" onClick={() => handleOrderById(row._id)}>
            {displayValue}
          </span>
        );
      }
    },
    {
  key: "recipientName",
  label: "Recepient Name",
  width: 120,
  render: (value, row) => {
    const shippingAddress = [
      row?.shippingAddress1,
      row?.shippingAddress2,
      row?.shippingAddress3,
      row?.shippingCity,
      row?.shippingState,
      row?.shippingPostalCode
    ].filter(Boolean).join(", ");

    const billingAddress = [
      row?.billingAddress1,
      row?.billingAddress2,
      row?.billingAddress3,
      row?.billingCity,
      row?.billingState,
      row?.billingPostalCode
    ].filter(Boolean).join(", ");

    const hasAddress = shippingAddress || billingAddress;

    const handleMouseEnter = (e) => {
      const tooltip = e.currentTarget.querySelector('.address-tooltip-card');
      if (tooltip) {
        const rect = e.currentTarget.getBoundingClientRect();
        tooltip.style.top = `${rect.bottom + 5}px`;
        tooltip.style.left = `${rect.left}px`;
      }
    };

    return (
      <div className="recipient-tooltip-wrapper" onMouseEnter={handleMouseEnter}>
        <span className="recipient-name">{value || "-"}</span>
        {hasAddress && (
          <div className="address-tooltip-card">
            {shippingAddress && (
              <div className="address-section">
                <strong>Shipping Address:</strong>
                <span>{shippingAddress}</span>
              </div>
            )}
            {billingAddress && (
              <div className="address-section">
                <strong>Billing Address:</strong>
                <span>{billingAddress}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
},
    {
      key: 'totalQuantity',
      label: 'Quantity',
      width: 100,
      render: (value, row) => (
        <span>
          {value} unit
        </span>
      )
    },
    {
      key: 'totalPrice',
      label: 'Importerr Price',
      width: 130,
      render: (value, row) => {
        const importerrPrice = calculateImporterrPrice(row);
        const shippingCharges = Number(row.totalShippingCharges) || 0;
        const imPointsDiscount = Number(row?.imPointsDiscount) || 0;
        const totalWithShipping = importerrPrice + shippingCharges;
        const grandTotal = totalWithShipping - imPointsDiscount;
        const formattedPrice = Number.isFinite(grandTotal)
          ? grandTotal.toFixed(2)
          : '0.00';
        return (
          <span
            className="price-cell"
          >
            ₹{formattedPrice}
          </span>
        );
      },
    },
    {
      key: 'imPointsDiscount',
      label: 'IM Points Discount',
      width: 150,
      render: (value, row) => {
        const imPointsDiscount = Number(row?.imPointsDiscount) || 0;
        if (imPointsDiscount > 0) {
          return (
            <span 
              className="price-cell"
              style={{ 
                color: '#4f46e5', 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <i className="fas fa-coins" style={{ fontSize: '12px' }}></i>
              -₹{imPointsDiscount.toFixed(2)}
            </span>
          );
        }
        return <span>-</span>;
      },
    },
    {
      key: 'marketplace',
      label: 'MKP Name',
      width: 120,
      render: (value, row) => (
        <span>
          {value}
        </span>
      )
    },
    {
      key: 'mkp_current_total_price',
      label: 'MKP Total Price',
      width: 140,
      render: (value, row) => {
        const totalPrice = Number(value) || 0;
        const shippingCharges = Number(row.totalShippingCharges) || 0;
        const imPointsDiscount = Number(row?.imPointsDiscount) || 0;
        const finalTotal = totalPrice + shippingCharges - imPointsDiscount;
        return (
          <span 
            className={`price-cell ${finalTotal > (calculateImporterrPrice(row) + shippingCharges - imPointsDiscount) ? 'price-positive' : ''}`}
          >
            {finalTotal ? '₹' + finalTotal.toFixed(2) : '₹0'}
          </span>
        );
      }
    },
    {
      key: 'mkp_financial_status',
      label: 'MKP Payment Status',
      width: 150,
      render: (value, row) => {
        // Get mkp_financial_status from last item in mkpOrderStatusHistory array
        let displayValue = value;
        if (Array.isArray(row?.mkpOrderStatusHistory) && row.mkpOrderStatusHistory.length > 0) {
          const lastItem = row.mkpOrderStatusHistory[row.mkpOrderStatusHistory.length - 1];
          displayValue = lastItem?.mkp_order_status || value || '';
        }
        return (
          <span className={`status-${displayValue?.toLowerCase().replace(' ', '-')}`}>
            {displayValue}
          </span>
        );
      }
    },
    {
      key: 'mkp_current_received_price',
      label: 'MKP Paid',
      width: 100,
      render: (value, row) => {
        const receivedPrice = Number(value) || 0;
        const shippingCharges = Number(row.totalShippingCharges) || 0;
        const imPointsDiscount = Number(row?.imPointsDiscount) || 0;
        const totalPaid = receivedPrice + shippingCharges - imPointsDiscount;
        return (
          <span className="price-cell">
            ₹{totalPaid.toFixed(2)}
          </span>
        );
      }
    },
    {
      key: 'mkp_current_outstanding_price',
      label: 'MKP Balance',
      width: 110,
      render: (value, row) => (
        <span className="price-cell">
          ₹{value || 0}
        </span>
      )
    },
    {
      key: 'orderDate',
      label: 'Order Date',
      width: 180,
      render: (value, row) => (
        <span>
          {moment(value).format('Do MMM YYYY, LT')}
        </span>
      )
    },
    {
      key: 'updatedAt',
      label: 'Updated at',
      width: 180,
      render: (value) => (
        <span>
          {value ? moment(value).format('Do MMM YYYY, LT') : '-'}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className={`status-${value?.toLowerCase().replace(' ', '-')}`}>
            {value}
          </span>
          {value === 'abnormal' && (
            <MdModeEdit 
              className="cursor-pointer text-blue-600 hover:text-blue-800" 
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row._id);
              }} 
            />
          )}
        </div>
      )
    }
  ];

  return (
    <div className="order_list_bg">
      {/* Status Tabs - New Modern Design */}
      <div className="seller-orders-tabs-wrapper">
        <div className="seller-orders-tabs-container">
          <div className="seller-orders-tabs-row">
            {options.map((item, index) => {
              const isActive = !showFailedOrders && (query.status || status) === item.value;
              return (
                <div
                  key={index}
                  className={`seller-orders-tab ${isActive ? 'seller-orders-tab-active' : 'seller-orders-tab-inactive'}`}
                  onClick={() => {
                    setShowFailedOrders(false);
                    handleOrderType(item.value);
                  }}
                >
                  <p className="seller-orders-tab-name">{item.name}</p>
                  <p className="seller-orders-tab-count">{item?.count || 0}</p>
                </div>
              );
            })}
            {/* 
              Failed Orders Tab - Commented out (access via sidebar only)
              {isOwnAccount && (
                <div
                  className={`seller-orders-tab seller-orders-tab-failed ${showFailedOrders ? 'seller-orders-tab-active' : 'seller-orders-tab-inactive'}`}
                  onClick={() => {
                    setShowFailedOrders(true);
                    setFailedPage(1);
                    fetchFailedOrders();
                  }}
                >
                  <p className="seller-orders-tab-name">Failed Orders</p>
                  <p className="seller-orders-tab-count">{failedOrdersCount}</p>
                </div>
              )}
            */}
          </div>
        </div>
      </div>

      {/* Search and Filter Component */}
      {/* Always show filter since Failed Orders tab is disabled */}
      <OrderFilter setQuery={setQuery} />

      {/* Upload & Actions Section */}
      <div className="seller-orders-filters-wrapper">
        <div className="seller-orders-upload-section">
          <div className="seller-orders-upload-row">
            <div className="seller-orders-file-input">
              <input
                ref={orderFileRef}
                type="file"
                onChange={handleOrdersFile}
              />
            </div>
            <button className="seller-orders-upload-btn" onClick={handleUploadOrders}>
              Upload
            </button>
          </div>
          {uploadFileErr && <span style={{ color: '#ef4444', fontSize: '12px' }}>{uploadFileErr}</span>}
          <div className="seller-orders-upload-links">
            <span className="seller-orders-upload-link" onClick={() => handleDownloadCsv()}>
              Check order file format
            </span>
            <span className="seller-orders-upload-link" onClick={() => downloadAbnormalOrders()}>
              Download abnormal orders
            </span>
          </div>
        </div>

        <div className="seller-orders-actions-section">
          {/* Add Order button commented out as requested */}
          {/* <button className="seller-orders-action-btn" onClick={handleUploadOneOrder}>
            Add Order
          </button> */}
          <button className="seller-orders-action-btn" onClick={handleExportAllOrders}>
            Export All Orders
          </button>
        </div>
      </div>

      {/* Show Regular Orders Table */}
      {/* Failed Orders tab is disabled - access via sidebar only */}
      <>
          <ExcelTable
            data={data}
            columns={tableColumns}
            // onRowClick={(row) => handleOrderById(row._id)}
            // onRowClick={undefined}   // disable full row click navigation
            onExport={handleExportAllOrders}
            onBulkAction={handleBulkAction}
            onSelectionChange={setSelectedOrders}
            selectedData={selectedOrders}
            sortable={true}
            resizable={true}
            selectable={true}
            showToolbar={true}
            bulkActions={getBulkActions(selectedOrders)}
            title="Orders Management"
          />

          {data.length ? <Pagination pages={pages} page={page} setPage={setPage} /> : null}
        </>

      {open ? <EditOrderPopup modalIsOpen={open} setIsOpen={setOpen} id={activeId} refresh={fetchOrders} /> : null}
      
      {ticketModalOpen ? (
        <RaiseTicket 
          modalIsOpen={ticketModalOpen} 
          setIsOpen={setTicketModalOpen} 
          id={activeTicketOrderId}
          orderIds={ticketOrderContext.bulkIds}
          bulkOrderIds={ticketOrderContext.bulkIds}
          processingOrderIds={ticketOrderContext.processingIds}
          handleRefresh={fetchOrders}
        />
      ) : null}

      {/* Cancel Order Modal */}
      <CancelOrderModal
        isOpen={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setCancelOrderData(null);
          setCancelOrdersData([]);
        }}
        order={cancelOrderData}
        orders={cancelOrdersData}
        onConfirm={handleCancelOrder}
        isLoading={isCancelling}
      />

      {/* Payment Modal */}
      <PaymentModal
        isOpen={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setPaymentOrderData(null);
          setPaymentOrdersData([]);
        }}
        order={paymentOrderData}
        orders={paymentOrdersData}
        onConfirm={handlePaymentConfirm}
        isLoading={isPayingOrder}
        walletBalance={walletBalance}
      />

      {/* Ticket chat panel (right side) */}
      {ticketPanelTicketId && (
        <TicketChatPanel
          ticketId={ticketPanelTicketId}
          orderLabel={ticketPanelOrderLabel}
          onClose={() => {
            setTicketPanelTicketId(null);
            setTicketPanelOrderLabel(null);
          }}
        />
      )}
    </div>
  );
};

export default Orders;
